/**
 * The desktop bridge.
 *
 * `@tauri-apps/api` is imported **dynamically**, so Vite splits it into a chunk
 * the browser build never fetches. Every function here is a no-op returning
 * null when the app is not running in the shell, which means callers can wire
 * up desktop affordances unconditionally and let them stay quiet on the web.
 *
 * Note what is not here: nothing that reads the filesystem, spawns a process,
 * or makes a network request outside the webview. The shell's capability list
 * does not grant those, and this file could not use them if it tried.
 */
import { isDesktop } from './runtime.js';

export interface DeviceIdentity {
  readonly deviceId: string;
  readonly platform: string;
  readonly label: string;
  readonly version: number;
}

export interface DesktopPrefs {
  readonly apiBaseUrl: string;
  readonly alwaysOnTop: boolean;
  readonly minimizeToTray: boolean;
  readonly checkUpdatesOnLaunch: boolean;
}

/** Rust serialises snake_case; the UI speaks camelCase. Converted once, here. */
function toCamel<T>(o: Record<string, unknown>): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    out[k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())] = v;
  }
  return out as T;
}

function toSnake(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    out[k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] = v;
  }
  return out;
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T | null> {
  if (!isDesktop()) return null;
  try {
    const { invoke: call } = await import('@tauri-apps/api/core');
    return (await call<T>(command, args)) as T;
  } catch {
    // A failed bridge call must never take the chart down with it. The desktop
    // affordance quietly does not happen; everything else keeps working.
    return null;
  }
}

export async function deviceIdentity(): Promise<DeviceIdentity | null> {
  const raw = await invoke<Record<string, unknown>>('get_device_identity');
  return raw ? toCamel<DeviceIdentity>(raw) : null;
}

export async function renameDevice(label: string): Promise<DeviceIdentity | null> {
  const raw = await invoke<Record<string, unknown>>('rename_device', { label });
  return raw ? toCamel<DeviceIdentity>(raw) : null;
}

export async function getPrefs(): Promise<DesktopPrefs | null> {
  const raw = await invoke<Record<string, unknown>>('get_prefs');
  return raw ? toCamel<DesktopPrefs>(raw) : null;
}

export async function setPrefs(prefs: DesktopPrefs): Promise<DesktopPrefs | null> {
  const raw = await invoke<Record<string, unknown>>('set_prefs', {
    prefs: toSnake(prefs as unknown as Record<string, unknown>),
  });
  return raw ? toCamel<DesktopPrefs>(raw) : null;
}

/**
 * Keep the window above the trading platform.
 *
 * The co-pilot posture: AEGIS is on screen while the user trades, beside the
 * platform they execute in. Off by default — see the note in prefs.rs.
 */
export async function setAlwaysOnTop(enabled: boolean): Promise<boolean | null> {
  return invoke<boolean>('set_always_on_top', { enabled });
}

/**
 * A panic recorded by a PREVIOUS launch of the shell.
 *
 * The Rust side cannot post its own report — see `crash.rs` — so it writes to
 * disk and the webview delivers it on the next start. Empty on the web, and
 * empty on a desktop launch that follows a clean exit, which is almost all of
 * them.
 */
export interface PanicRecord {
  readonly message: string;
  /** `src/device.rs:118:9` */
  readonly location: string;
  readonly thread: string;
  /** The version that PANICKED, which is not necessarily the one running now. */
  readonly appVersion: string;
  readonly occurredAt: string;
}

export async function takePendingPanics(): Promise<readonly PanicRecord[]> {
  return (await invoke<PanicRecord[]>('take_pending_panics')) ?? [];
}
